import * as React from 'react';
import { useDispatch } from 'react-redux';

import { IActiveFilter } from './types';
import { useFiltersContext } from '../../../../packages/Filters/shared/utils/useFiltersContext';
import {
  getRangeValue,
  getTermsValue,
  getTermValue,
  resetTerms,
  TJsonQuery,
  TJsonQueryKeys,
  TJsonQueryOptionalKeys,
} from '../../../../packages/JsonQuery';
import { justChangeJsonQuery } from '../../actions/filters/justChangeJsonQuery';
import { COMMON_FILTERS } from '../../constants/activeFilters';
import { TThunkDispatch } from '../../types/redux';
import { getPriceLabel } from '../../utils/getPriceLabel';
import { getRoomTypeLabels } from '../../utils/getRoomTypeLabel';

const hasExtraFiltersUtil = (jsonQuery: TJsonQuery): boolean => {
  const jsonQueryKeys = Object.keys(jsonQuery) as TJsonQueryKeys[];

  return jsonQueryKeys.some(key => {
    const isCommonFilter = COMMON_FILTERS.includes(key);
    const hasFilterValue = jsonQuery[key] !== undefined;

    return !isCommonFilter && hasFilterValue;
  });
};

interface IUseActiveFiltersProps {
  onReset?(): void;
}

interface IUseActiveFiltersResult {
  activeFilters: IActiveFilter[];
  hasActiveFilters: boolean;
  hasExtraFilters: boolean;
}

// Хук для получения списка активных фильтров
export function useActiveFilters({ onReset }: IUseActiveFiltersProps = {}): IUseActiveFiltersResult {
  const dispatch = useDispatch<TThunkDispatch>();
  const { jsonQuery, onApply } = useFiltersContext();
  const hasExtraFilters = React.useMemo(() => hasExtraFiltersUtil(jsonQuery), [jsonQuery]);

  // Обработчик сброса фильтров
  const createResetHandler = React.useCallback(
    (termsToReset: TJsonQueryOptionalKeys[]) => {
      return () => {
        const nextJsonQuery = resetTerms(termsToReset)(jsonQuery);
        dispatch(justChangeJsonQuery({ nextJsonQuery }));
        onReset?.();
        onApply();
      };
    },
    [dispatch, jsonQuery, onReset, onApply],
  );

  return React.useMemo(() => {
    const activeFilters: IActiveFilter[] = [];

    // Цена
    const price = getRangeValue('price')(jsonQuery);
    if (price && (price.gte !== undefined || price.lte !== undefined)) {
      const { gte: priceMin = null, lte: priceMax = null } = price;
      const label = `Цена ${getPriceLabel(priceMin, priceMax)}`;

      activeFilters.push({
        id: 'price',
        label,
        onReset: createResetHandler(['price']),
      });
    }

    // Комнатность
    const roomType = getTermsValue('room')(jsonQuery);
    if (roomType && roomType.length > 0) {
      const label = getRoomTypeLabels(roomType);

      activeFilters.push({
        id: 'roomType',
        label,
        onReset: createResetHandler(['room']),
      });
    }

    // Мгновенная бронь
    const onlineBooking = getTermValue('online_booking')(jsonQuery);
    if (onlineBooking === true) {
      activeFilters.push({
        id: 'onlineBooking',
        label: 'Мгновенное бронирование',
        onReset: createResetHandler(['online_booking']),
      });
    }

    // Дети
    const kidsAllowed = getTermValue('kids')(jsonQuery);
    if (kidsAllowed === true) {
      activeFilters.push({
        id: 'kidsAllowed',
        label: 'Можно с детьми',
        onReset: createResetHandler(['kids']),
      });
    }

    // Животные
    const petsAllowed = getTermValue('pets')(jsonQuery);
    if (petsAllowed === true) {
      activeFilters.push({
        id: 'petsAllowed',
        label: 'Можно с животными',
        onReset: createResetHandler(['pets']),
      });
    }

    // От собственника
    const fromHomeowner = getTermValue('is_by_homeowner')(jsonQuery);
    if (fromHomeowner === true) {
      activeFilters.push({
        id: 'fromHomeowner',
        label: 'От собственника',
        onReset: createResetHandler(['is_by_homeowner']),
      });
    }

    // Без залога
    const deposit = getTermValue('zalog')(jsonQuery);
    if (deposit === false) {
      activeFilters.push({
        id: 'noDeposit',
        label: 'Без залога',
        onReset: createResetHandler(['zalog']),
      });
    }

    const hasActiveFilters = activeFilters.length > 0;

    return {
      activeFilters,
      hasActiveFilters,
      hasExtraFilters,
    };
  }, [jsonQuery, hasExtraFilters, createResetHandler]);
}
